/**
 *  @class
 *  @function PredictiveSearch
 */
class PredictiveSearch {
  constructor() {
    this.container = document.getElementById('Search-Drawer');
    this.form = this.container.querySelector('.search-drawer--searchform');
    this.button = document.querySelectorAll('.thb-quick-search');
    this.input = this.container.querySelector('input[type="search"]');
    this.predictiveSearchResultsWrapper = this.container.querySelector('.search-drawer--inner');
    this.predictiveSearchResults = this.predictiveSearchResultsWrapper.querySelector('.thb-predictive-search');
    this.status = this.container.querySelector('#Search-Drawer-Status');
    this.background = Array.from(document.querySelectorAll(
      'theme-header, #main-content, .shopify-section-group-footer-group, #Cart-Drawer'
    ));
    this.boundOnKeydown = this.onKeydown.bind(this);
    this.opener = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));

    this.button.forEach((item, i) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        if (document.body.classList.contains('open-search')) {
          this.close({ restoreFocus: true });
        } else {
          this.open(item);
        }

        return false;
      });
    });

    document.addEventListener('panel:close', () => {
      if (this.container.getAttribute('aria-hidden') === 'false') {
        this.close({ restoreFocus: true });
      }
    });
  }

  getFocusableElements() {
    return Array.from(this.container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [role="button"][tabindex="0"], [tabindex]:not([tabindex="-1"])'
    )).filter((element) => element.getClientRects().length > 0);
  }

  setBackgroundInert(isInert) {
    this.background.forEach((element) => {
      element.inert = isInert;
    });
  }

  open(opener) {
    this.opener = opener;
    document.body.classList.add('open-search', 'open-cc');
    this.container.removeAttribute('inert');
    this.container.setAttribute('aria-hidden', 'false');
    this.button.forEach((item) => {
      item.setAttribute('aria-expanded', (item === opener).toString());
    });
    this.setBackgroundInert(true);
    this.container.addEventListener('keydown', this.boundOnKeydown);
    setTimeout(() => {
      this.input.focus({ preventScroll: true });
    }, 100);
    dispatchCustomEvent('search:open');
  }

  onKeydown(event) {
    if (event.key !== 'Tab') return;

    const focusableElements = this.getFocusableElements();
    if (!focusableElements.length) {
      event.preventDefault();
      this.container.focus();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.abortPendingRequest();
      this.container.classList.remove('active');
      this.predictiveSearchResults.classList.remove('loading');
      this.predictiveSearchResults.removeAttribute('aria-busy');
      this.predictiveSearchResults.innerHTML = '';
      if (this.status) this.status.textContent = '';
      return;
    }
    this.container.classList.add('active');
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    this.getSearchResults(searchTerm);
  }

  abortPendingRequest() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  getSearchResults(searchTerm) {
    this.abortPendingRequest();
    const controller = new AbortController();
    this.abortController = controller;

    this.predictiveSearchResults.classList.add('loading');
    this.predictiveSearchResults.setAttribute('aria-busy', 'true');

    fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,collection,article,query&${encodeURIComponent('resources[limit]')}=6&section_id=predictive-search`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (this.abortController !== controller) return;
        const resultsSection = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!resultsSection) throw new Error('Predictive search response was missing its section');

        this.renderSearchResults(resultsSection.innerHTML);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(error);
        if (this.abortController !== controller) return;
        this.renderError(searchTerm);
      })
      .finally(() => {
        if (this.abortController !== controller) return;
        this.abortController = null;
        this.predictiveSearchResults.classList.remove('loading');
        this.predictiveSearchResults.removeAttribute('aria-busy');
      });
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    let _this = this,
      submitButton = this.container.querySelector('#search-results-submit');
    const resultStatus = this.predictiveSearchResults.querySelector('.predictive-search-status');
    const resultCount = this.predictiveSearchResults.querySelectorAll(
      'product-card, .thb-predictive-search--collection, .thb-predictive-search--article, .thb-predictive-search--query'
    ).length;
    if (this.status && resultStatus) this.status.textContent = resultStatus.textContent.trim();
    window.HideawayAnalytics?.trackSearchState({
      searchSurface: 'drawer',
      resultState: resultCount > 0 ? 'results' : 'no_results',
      resultCount,
      query: this.getQuery()
    });

    if (document.body.classList.contains('animations-true')) {
      gsap.to(this.predictiveSearchResults.querySelectorAll('.product-card .product-featured-image-link'), { scale: 1, opacity: 1, stagger: 0.15, ease: window.theme.settings.animation_easing });
    }
    if (submitButton) {
      submitButton.addEventListener('click', () => {
        _this.form.submit();
      });
    }
  }

  renderError(searchTerm) {
    this.predictiveSearchResults.innerHTML = '';
    const alert = document.createElement('div');
    alert.setAttribute('role', 'alert');
    const message = document.createElement('p');
    message.textContent = this.container.dataset.errorMessage;
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'button';
    retry.textContent = this.container.dataset.retryLabel;
    retry.addEventListener('click', () => this.getSearchResults(searchTerm), { once: true });
    alert.append(message, retry);
    this.predictiveSearchResults.appendChild(alert);
    if (this.status) this.status.textContent = this.container.dataset.errorMessage;
    window.HideawayAnalytics?.trackSearchState({
      searchSurface: 'drawer',
      resultState: 'error',
      resultCount: 0,
      query: searchTerm
    });
  }

  close({ restoreFocus = false } = {}) {
    this.container.classList.remove('active');
    document.body.classList.remove('open-search', 'open-cc');
    this.container.setAttribute('aria-hidden', 'true');
    this.container.setAttribute('inert', '');
    this.button.forEach((item) => item.setAttribute('aria-expanded', 'false'));
    this.setBackgroundInert(false);
    this.container.removeEventListener('keydown', this.boundOnKeydown);
    if (restoreFocus) this.opener?.focus();
  }
}
window.addEventListener('load', () => {
  if (typeof PredictiveSearch !== 'undefined') {
    new PredictiveSearch();
  }
});
